import VisibilityIcon from '@mui/icons-material/Visibility';
import CircularProgress from '@mui/material/CircularProgress';
import { GridActionsCellItem } from '@mui/x-data-grid';
import moment from 'moment';
import 'moment/locale/vi';
import {
  lazy,
  memo,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Loading from '../components/layouts/Loading';
import { AuthContext } from '../contexts/AuthContext';
import { showModal } from '../redux/actions';
import { getOrders, getUserOrders } from '../redux/actions/orders';
import { orders$, toast$ } from '../redux/selectors';
moment.locale('vi');

const DataTable = lazy(() => import('../components/layouts/DataTable'));
const AddModal = lazy(() => import('../components/orders/AddModal'));
const RefundModal = lazy(() => import('../components/orders/RefundModal'));
const UserOderDetailModal = lazy(() =>
  import('../components/orders/UserOderDetailModal')
);

const OrdersHistory = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const toast = useSelector(toast$);
  const orders = useSelector(orders$);
  const [openDetail, setOpenDetail] = useState(false);
  const [openRefund, setOpenRefund] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const {
    authState: { isAuthenticated, authLoading },
  } = useContext(AuthContext);
  const { t } = useTranslation();

  if (!authLoading && !isAuthenticated) {
    navigate(`/login?RedirectTo=${location.pathname}${location.search}`);
  }

  useEffect(() => {
    if (orders.userOrders.length === 0 && isAuthenticated && !authLoading) {
      dispatch(getUserOrders.getUserOrdersRequest());
    }
    if (orders.data.length === 0 && isAuthenticated && !authLoading) {
      dispatch(getOrders.getOrdersRequest());
    }
  }, [
    authLoading,
    dispatch,
    isAuthenticated,
    orders.data.length,
    orders.userOrders.length,
  ]);

  const setShowModal = useCallback(() => {
    dispatch(showModal());
  }, [dispatch]);

  const handleChangeRowsPerPage = useCallback((newPageSize) => {
    setRowsPerPage(newPageSize);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setSelectedId('');
    setOpenDetail(false);
  }, []);

  const handleSeeDetailClick = useCallback(
    (id) => (event) => {
      event.stopPropagation();
      setSelectedId(id);
      setOpenDetail(true);
    },
    []
  );

  const handleCloseRefund = useCallback(() => {
    setSelectedId('');
    setOpenRefund(false);
  }, []);

  if (orders.loading) {
    return (
      <div
        style={{
          left: '50%',
          top: '50%',
          transform: 'translateY(-50%)',
          position: 'absolute',
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  const columns = [
    { field: 'id', headerName: t('orderCode'), minWidth: 160, flex: 1 },
    {
      field: 'isDelivery',
      headerName: t('deliveryStatus'),
      minWidth: 130,
      flex: 1,
      valueGetter: (param) => {
        return param.value === 2
          ? t('delivered')
          : param.value === 0
          ? t('notDeliveredYet')
          : param.value === 1
          ? t('delivering')
          : t('cancelled');
      },
    },
    {
      field: 'isPayment',
      headerName: t('paymentStatus'),
      minWidth: 120,
      flex: 1,
      valueGetter: (param) => {
        return param.value === 1
          ? t('paid')
          : param.value === 0
          ? t('notPaidYet')
          : t('refunds');
      },
    },
    {
      field: 'totalPrice',
      headerName: t('total'),
      minWidth: 120,
      flex: 1,
      valueGetter: (param) => {
        return `${String(param.value).replace(/(.)(?=(\d{3})+$)/g, '$1,')} ₫`;
      },
    },
    {
      field: 'createdAt',
      headerName: t('dateCreated'),
      type: 'date',
      minWidth: 100,
      flex: 1,
      valueGetter: (param) => {
        return `${moment(param.value).format('l')}`;
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: t('viewDetails'),
      minWidth: 100,
      flex: 1,
      cellClassName: 'actions',
      getActions: ({ id }) => [
        <GridActionsCellItem
          icon={<VisibilityIcon color="info" />}
          label="Watch"
          className="textPrimary"
          onClick={handleSeeDetailClick(id)}
          color="inherit"
        />,
      ],
    },
  ];

  return (
    <>
      {/* <!-- Breadcrumb Section Begin --> */}
      <section
        className="breadcrumb-section set-bg"
        style={{
          backgroundImage: `url(img/breadcrumb.jpg)`,
        }}
      >
        <div className="container">
          <div className="row">
            <div className="col-lg-12 text-center">
              <div className="breadcrumb__text">
                <h2>{t('orderHistory.title')}</h2>
                <div className="breadcrumb__option">
                  <Link to="/">{t('header.home')}</Link>
                  <span>{t('orderHistory.title')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* <!-- Breadcrumb Section End --> */}
      <Suspense fallback={<Loading />}>
        <UserOderDetailModal
          open={openDetail}
          id={selectedId}
          handleClose={handleCloseDetail}
          setOpenRefund={setOpenRefund}
          setOpenDetail={setOpenDetail}
        />
      </Suspense>

      <Suspense fallback={<Loading />}>
        <RefundModal
          open={openRefund}
          id={selectedId}
          handleClose={handleCloseRefund}
          orders={orders}
        />
      </Suspense>

      <section>
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <Suspense fallback={<Loading />}>
                <DataTable
                  component={AddModal}
                  toast={toast}
                  data={orders.userOrders}
                  columns={columns}
                  rowsPerPage={rowsPerPage}
                  handleChangeRowsPerPage={handleChangeRowsPerPage}
                  setShowModal={setShowModal}
                />
              </Suspense>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default memo(OrdersHistory);
